import React from 'react';
import { ValidationIndicator } from './ValidationIndicator';

interface AuditorsInformationTableProps {
  field: any;
  companyInfo: any;
  formData: Record<string, any>;
  onFieldChange: (name: string, contextRef: string, value: string, qname?: string) => void;
  // hasFieldError/getFieldError removed - validation now handled via DOM manipulation
}

// Helper to format date strings (same as SOFPTable)
const formatDateForContext = (dateStr: string): string => {
  if (/^\d{8}$/.test(dateStr)) {
    return dateStr;
  }
  return dateStr.replace(/-/g, '');
};

// Helper function to get current period context with member (same as SOFPTable)
const getPeriodContext = (item: any, companyInfo: any, member: string): string => {
  const period = companyInfo.currentPeriod || companyInfo.reporting_period;
  if (!period) {
    return `context_${member}`;
  }

  const formattedStart = formatDateForContext(period.start);
  const formattedEnd = formatDateForContext(period.end);
  const cleanMember = member.startsWith('_') ? member.substring(1) : member;
  const memberSuffix = cleanMember ? `_${cleanMember}` : '';

  // Build context ref based on period_type
  if (item.properties?.period_type === 'duration') {
    return `fromto_${formattedStart}_${formattedEnd}${memberSuffix}`;
  } else {
    // Default to instant period type (asof_end_date)
    return `asof_${formattedEnd}${memberSuffix}`;
  }
};

/**
 * AuditorsInformationTable - Handles auditor information section
 * Matches 1.0 logic from form_macros-tailwind.html lines 450-586
 */
const AuditorsInformationTableComponent: React.FC<AuditorsInformationTableProps> = ({
  field,
  companyInfo,
  formData,
  onFieldChange
}) => {
  // Error indicators now handled by DOM manipulation in useValidation.ts (no React rendering needed)
  
  // Helper function to get auditor context (matches 1.0 get_period_context with _AuditorsCountAxis_0001)
  const getAuditorContext = (item: any) => {
    // Use the same context generation logic as SOFPTable
    return getPeriodContext(item, companyInfo, '_AuditorsCountAxis_0001');
  };

  // Render individual auditor field
  const renderAuditorField = (item: any, index: number) => {
    const contextRef = getAuditorContext(item);
    // Use qname + contextRef as the key (same as other components)
    const dataKey = `${item.qname}_${contextRef}`;
    const currentValue = formData[dataKey] || '';

    const handleChange = (value: string) => {
      onFieldChange(item.name, contextRef, value, item.qname);
    };

    // Special handling for StateWhereAuditFirmIsLocated (matches lines 465-501)
    if (item.qname === 'ssmt:StateWhereAuditFirmIsLocated') {
      return (
        <div key={`${item.name}_${index}`} className="form-group row">
          <div className="col-2">
            <label className="form-label">{item.standard_label}</label>
          </div>
          <div className="col-2">
            <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
              <select
                className="form-control"
                name={item.name}
                data-qname={item.qname}
                data-period-type={item.properties?.period_type}
                data-type={item.properties?.data_type}
                data-balance={item.properties?.balance}
                data-context-ref={contextRef}
                defaultValue={currentValue}
                onChange={(e) => handleChange(e.target.value)}
              >
                <option value="">Please Select</option>
                <option value="W.P. PUTRAJAYA">W.P. PUTRAJAYA</option>
                <option value="W.P. KUALA LUMPUR">W.P. KUALA LUMPUR</option>
                <option value="W.P. LABUAN">W.P. LABUAN</option>
                <option value="SARAWAK">SARAWAK</option>
                <option value="SABAH">SABAH</option>
                <option value="JOHOR">JOHOR</option>
                <option value="MELAKA">MELAKA</option>
                <option value="NEGERI SEMBILAN">NEGERI SEMBILAN</option>
                <option value="PAHANG">PAHANG</option>
                <option value="SELANGOR">SELANGOR</option>
                <option value="PERAK">PERAK</option>
                <option value="TERENGGANU">TERENGGANU</option>
                <option value="KELANTAN">KELANTAN</option>
                <option value="PULAU PINANG">PULAU PINANG</option>
                <option value="KEDAH">KEDAH</option>
                <option value="PERLIS">PERLIS</option>
              </select>
              <ValidationIndicator fieldName={item.name} contextRef={contextRef} />
            </div>
          </div>
        </div>
      );
    }

    // Default text input for other auditor fields (matches lines 503-518)
    return (
      <div key={`${item.name}_${index}`} className="form-group row">
        <div className="col-2">
          <label className="form-label">{item.standard_label}</label>
        </div>
        <div className="col-2">
          <div style={{ position: 'relative', width: '100%' }}>
            <input
              type="text"
              className="form-control"
              name={item.name}
              data-qname={item.qname}
              data-period-type={item.properties?.period_type}
              data-type={item.properties?.data_type}
              data-balance={item.properties?.balance}
              data-context-ref={contextRef}
              placeholder={`Enter ${item.standard_label}`}
              defaultValue={currentValue}
              onChange={(e) => handleChange(e.target.value)}
            />
            <ValidationIndicator fieldName={item.name} contextRef={contextRef} />
          </div>
        </div>
      </div>
    );
  };

  // Recursive function to render auditor fields (matches 1.0 nested logic)
  const renderAuditorChildren = (children: any[]) => {
    return children.map((child, childIndex) => {
      // Handle abstract children with their own children (matches lines 459-583)
      if (child.properties?.abstract && child.structure?.children) {
        return (
          <React.Fragment key={`${child.name}_${childIndex}`}>
            {child.structure.children.map((item: any, itemIndex: number) => {
              if (!item.properties?.abstract) {
                // Direct field rendering
                return renderAuditorField(item, itemIndex);
              } else if (item.properties?.abstract && item.structure?.children) {
                // Handle nested abstract sections (matches lines 520-580)
                return (
                  <React.Fragment key={`${item.name}_${itemIndex}`}>
                    {item.structure.children.map((nestedItem: any, nestedIndex: number) => {
                      if (!nestedItem.properties?.abstract) {
                        return renderAuditorField(nestedItem, nestedIndex);
                      }
                      return null;
                    })}
                  </React.Fragment>
                );
              }
              return null;
            })}
          </React.Fragment>
        );
      }
      
      // Direct field rendering for non-abstract children
      if (!child.properties?.abstract) {
        return renderAuditorField(child, childIndex);
      }
      
      return null;
    });
  };

  // Main render - matches the structure from lines 452-586
  return (
    <div className="auditors-information-section">
      {field.structure?.children && renderAuditorChildren(field.structure.children)}
    </div>
  );
};

// Memoize to prevent unnecessary re-renders when parent state changes
export const AuditorsInformationTable = React.memo(AuditorsInformationTableComponent);